import React, { useState } from "react";
import { deleteRepairListing, pauseRepairListing, reactivateRepairListing } from "../lib/repairListings";
import { startFeaturedListingCheckout } from "../lib/subscription";
import { T } from "../theme";
import { Btn, Card, SectionTitle } from "../ui";

const FEATURED_REPAIR_PRICE_ID = process.env.REACT_APP_STRIPE_PRICE_FEATURED_REPAIR;

export default function RepairListingDetailScreen({ listing, isMine, onBack }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [featuring, setFeaturing] = useState(false);
  const [error, setError] = useState("");

  const featuredUntilDate = listing.featuredUntil?.toDate ? listing.featuredUntil.toDate() : null;
  const isFeatured = !!(featuredUntilDate && featuredUntilDate > new Date());

  const remove = async () => {
    setBusy(true);
    try {
      await deleteRepairListing(listing.id);
      onBack();
    } finally {
      setBusy(false);
    }
  };

  const togglePaused = async () => {
    setBusy(true);
    try {
      if (listing.status === "paused") await reactivateRepairListing(listing.id);
      else await pauseRepairListing(listing.id);
    } finally {
      setBusy(false);
    }
  };

  const feature = async () => {
    setError("");
    setFeaturing(true);
    try {
      await startFeaturedListingCheckout(listing.id, FEATURED_REPAIR_PRICE_ID);
    } catch (e) {
      setError(e.message || "Could not start checkout.");
      setFeaturing(false);
    }
  };

  return (
    <div>
      <Card style={{ background: `linear-gradient(135deg,${T.surface},${T.card})`, textAlign: "center", padding: "24px 16px" }}>
        <div style={{ fontSize: 22, fontWeight: "bold", color: T.text }}>{listing.providerName}</div>
        <div style={{ marginTop: 8, display: "flex", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
          {isFeatured && <span style={{ fontSize: 9, background: T.accentAlt + "22", border: `1px solid ${T.accentAlt}55`, color: T.value, fontWeight: "bold", padding: "2px 8px", borderRadius: 4 }}>⭐ FEATURED</span>}
          {listing.status === "paused" && <span style={{ fontSize: 9, color: T.muted }}>PAUSED</span>}
        </div>
      </Card>

      {(listing.photoUrls || []).length > 0 && (
        <>
          <SectionTitle>Photos</SectionTitle>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
            {listing.photoUrls.map((url, i) => (
              <img key={i} src={url} alt="" style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8, border: `1px solid ${T.border}` }} />
            ))}
          </div>
        </>
      )}

      <SectionTitle>Services</SectionTitle>
      <Card>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {(listing.services || []).map((s) => (
            <span key={s} style={{ fontSize: 11, background: T.statBg, border: `1px solid ${T.border}`, color: T.text, padding: "4px 10px", borderRadius: 20 }}>{s}</span>
          ))}
        </div>
        {(listing.priceRange || listing.turnaroundTime) && (
          <div style={{ marginTop: 10, fontSize: 12, color: T.muted }}>
            {listing.priceRange && <div>Price range: {listing.priceRange}</div>}
            {listing.turnaroundTime && <div>Turnaround: {listing.turnaroundTime}</div>}
          </div>
        )}
      </Card>

      <SectionTitle>Description &amp; Contact</SectionTitle>
      <Card><div style={{ fontSize: 13, color: T.muted, lineHeight: 1.7 }}>{listing.description}</div></Card>

      {isMine && (
        <Card style={{ background: isFeatured ? undefined : `linear-gradient(135deg,${T.surface},${T.statBg})` }}>
          {isFeatured ? (
            <div style={{ fontSize: 12, color: T.muted }}>Featured until {featuredUntilDate.toLocaleDateString()}.</div>
          ) : (
            <>
              <div style={{ fontSize: 13, fontWeight: "bold", color: T.text, marginBottom: 4 }}>⭐ Get Featured</div>
              <div style={{ fontSize: 11, color: T.muted, marginBottom: 10 }}>Boost this listing to the top of the Repair directory for 30 days.</div>
              {error && <div style={{ color: "#C0392B", fontSize: 12, marginBottom: 10 }}>{error}</div>}
              <Btn variant="gold" disabled={featuring} onClick={feature} style={{ width: "100%" }}>{featuring ? "…" : "Feature This Listing"}</Btn>
            </>
          )}
        </Card>
      )}

      {isMine && confirming ? (
        <Card style={{ textAlign: "center" }}>
          <div style={{ fontSize: 13, color: T.text, marginBottom: 12 }}>Remove this listing? This cannot be undone.</div>
          <div style={{ display: "flex", gap: 10 }}>
            <Btn variant="outline" onClick={() => setConfirming(false)} style={{ flex: 1 }}>Cancel</Btn>
            <Btn variant="danger" disabled={busy} onClick={remove} style={{ flex: 1 }}>{busy ? "Removing…" : "Remove"}</Btn>
          </div>
        </Card>
      ) : (
        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <Btn variant="outline" onClick={onBack} style={{ flex: 1 }}>← Back</Btn>
          {isMine && (
            <>
              <Btn disabled={busy} onClick={togglePaused} style={{ flex: 1 }}>{listing.status === "paused" ? "Reactivate" : "Pause"}</Btn>
              <Btn variant="danger" onClick={() => setConfirming(true)} style={{ flex: 1 }}>Remove</Btn>
            </>
          )}
        </div>
      )}
      <div style={{ height: 20 }} />
    </div>
  );
}
